<template>
  <ion-page>
    <ion-content class="ion-padding-start ion-padding-end mt-12">
      <div
        v-if="loading"
        class="is-flex ion-justify-content-center ion-align-items-center h-100"
      >
        <IonSpinner />
      </div>

      <div
        v-if="
          !loading &&
          !searchInput &&
          !filteredCourses.length &&
          !filteredStreams.length
        "
        class="h-100 is-flex ion-justify-content-center ion-align-items-center"
      >
        {{ $t('searchPage.startSearch') }}
      </div>

      <div
        v-if="!loading && searchInput && !searchCourses.length"
        class="h-100 is-flex ion-justify-content-center ion-align-items-center"
      >
        {{ $t('searchPage.noResults') }} «{{ searchInput }}»
      </div>

      <section class="mt-large">
        <template v-if="filteredSeries.length">
          <h2 class="my-4">
            {{ $t('searchPage.podcasts') }}
          </h2>

          <swiper :slides-per-view="'auto'" :space-between="30">
            <swiper-slide
              v-for="(course, i) in filteredSeries"
              :key="`item-${i}`"
              @click="$router.push(`/course/${course.id}`)"
            >
              <AppImg
                :img-src="banner(course)"
                class="img-wrap"
                border-radius="6px"
              />
              <h4 class="title">{{ course.course_title }}</h4>
              <p class="subtitle">{{ course.company.name }}</p>
            </swiper-slide>
          </swiper>
        </template>

        <div v-if="filteredStreams.length" class="stream-list">
          <h2 class="my-4">
            {{ $t('searchPage.episodes') }}
          </h2>
          <SinglePodcast
            class="stream-card mb-4"
            v-for="(stream, index) in filteredStreams"
            :key="`stream-filtered-${index}`"
            :podcast-data="stream"
            :course-media="getCourseMedia(stream)"
            @click="openPodcast(stream)"
          />
        </div>
      </section>

      <section class="mt-large stream-list">
        <h2
          v-if="userCollectionCourses.length || userCollectionStream.length"
          class="mt-6"
        >
          {{ $t('searchPage.myCollection') }}
        </h2>

        <SeriesPodcast
          v-for="(course, index) in userCollectionCourses"
          :key="`course-series-${index}`"
          :course-data="course"
          @click="$router.push(`/course/${course.id}`)"
        />

        <SinglePodcast
          v-for="(stream, index2) in userCollectionStream"
          :key="`stream-${index2}`"
          :podcast-data="stream"
          :course-media="getCourseMedia(stream)"
          @click="openPodcast(stream)"
        />
      </section>
    </ion-content>
  </ion-page>
</template>

<script>
import { computed } from '@vue/reactivity';

import {
  IonContent,
  IonPage,
  IonToolbar,
  IonBackButton,
  IonSearchbar,
  IonHeader,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonButtons,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
} from '@ionic/vue';
import { useStore } from 'vuex';

import SeriesPodcast from '@/components/common/SeriesPodcast';
import SinglePodcast from '@/components/common/SinglePodcast';

import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import AppImg from '@/components/common/AppImg.vue';
import { getSmallestSizeImage } from '@/helpers/functions';

export default {
  name: 'SearchPage',
  components: {
    IonPage,
    IonCard,
    IonHeader,
    IonContent,
    IonButtons,
    IonSpinner,
    IonToolbar,
    IonCardTitle,
    IonSearchbar,
    IonBackButton,
    IonCardHeader,
    IonCardContent,
    IonCardSubtitle,
    SeriesPodcast,
    SinglePodcast,
    AppImg,
    Swiper,
    SwiperSlide,
  },
  props: {
    searchInput: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const store = useStore();

    const searchCourses = computed(
      () => store.getters['search/getSearchCourses'],
    );
    const userCollectionCourses = computed(
      () => store.getters['search/getUserCollectionCourses'],
    );
    const userCollectionStream = computed(
      () => store.getters['search/getUserCollectionStreams'],
    );
    const filteredCourses = computed(
      () => store.getters['search/getFilteredCourses'],
    );
    const filteredStreams = computed(
      () => store.getters['search/getFilteredStreams'],
    );

    const filteredSeries = computed(() =>
      filteredCourses.value.filter((course) => {
        return course.entity_type === 'series';
      }),
    );

    const slideOptions = {
      slidesPerView: 'auto',
    };

    const openPlayer = (stream, course) => {
      const startAt = course.streams.findIndex(
        (companyStream) => companyStream.id === stream.id,
      );
      store.dispatch('player/openPlayer', {
        course,
        company: course.company,
        playlist: course.streams,
        startAt,
      });
    };

    const openPodcast = (stream) => {
      store
        .dispatch('course/setCourseData', stream.course_id)
        .then((course) => {
          openPlayer(stream, course);
        });
    };

    const getCourseMedia = (stream) => {
      const rawFavoritesList = store.state.search.courses ?? [];
      return (
        rawFavoritesList.find((course) => course.id === stream.course_id)
          ?.media ?? []
      );
    };

    function banner(data) {
      return getSmallestSizeImage(data?.media)?.url ?? null;
    }

    return {
      searchCourses,
      filteredStreams,
      filteredCourses,
      filteredSeries,
      userCollectionCourses,
      userCollectionStream,

      openPodcast,
      slideOptions,
      banner,
      getCourseMedia,
    };
  },
};
</script>

<style scoped lang="scss">
.mt-large {
  margin-top: 70px !important;
}

.swiper {
  width: 100%;
  .swiper-slide {
    cursor: pointer;
    width: 200px;
    .img-wrap {
      height: 200px;
      width: 200px;
      border-radius: 6px;
      :deep(img) {
        width: 100%;
        height: 100%;
      }
    }
    .title {
      font-size: 14px;
      margin-top: 10px;
      text-align: left;
    }
    .subtitle {
      font-size: 10px;
      margin-top: 10px;
      text-align: left;
    }
  }
}
.stream-list {
  width: 100%;
  max-width: 960px;
  h2 {
    text-align: left;
  }
}
</style>
